import React from 'react';
import { useT } from '../i18n';

/** Direction emoji icons for sidebar */
import { TrendingUp, ShoppingCart, GraduationCap, Heart, BarChart4,
  Monitor, Palette, Building2, Film, Home, Plane, Utensils, Scale,
  Briefcase, Bot, Sparkles, Search, Dumbbell, Car, Factory, Sprout,
  HardHat, Hotel, Headphones, Users, HandHeart, Megaphone } from 'lucide-react';

export const DIRECTION_ICONS = {
  sales: TrendingUp,
  finance: BarChart4,
  healthcare: Heart,
  tech: Monitor,
  it_software: Monitor,
  real_estate: Home,
  realestate: Home,
  legal: Scale,
  media_entertainment: Film,
  media: Film,
  education: GraduationCap,
  consulting: Briefcase,
  marketing: Megaphone,
  customer_service: Headphones,
  hr: Users,
  ndis_care: HandHeart,
  ecommerce: ShoppingCart,
  hospitality: Hotel,
  construction: HardHat,
  manufacturing: Factory,
  agriculture: Sprout,
  fitness_wellness: Dumbbell,
  automotive: Car,
  beauty_salon: Sparkles,
  research: Search,
  social: TrendingUp,
  creative: Palette,
  service: Building2,
  travel: Plane,
  food: Utensils,
  health: Heart,
  default: Sparkles,
};

/** Direction accent colors for avatar badges & dots */
export const DIRECTION_COLORS = {
  sales: '#6366f1',
  finance: '#10b981',
  healthcare: '#ef4444',
  health: '#ef4444',
  ndis_care: '#ec4899',
  consulting: '#0ea5e9',
  customer_service: '#f59e0b',
  education: '#8b5cf6',
  tech: '#3b82f6',
  it_software: '#3b82f6',
  ecommerce: '#f97316',
  marketing: '#e11d48',
  hr: '#14b8a6',
  legal: '#64748b',
  real_estate: '#06b6d4',
  realestate: '#06b6d4',
  hospitality: '#d946ef',
  creative: '#a855f7',
  media: '#db2777',
  media_entertainment: '#db2777',
  research: '#0891b2',
  social: '#6366f1',
  service: '#0ea5e9',
  default: '#6366f1',
};

export function getDirectionColor(direction) {
  return DIRECTION_COLORS[direction] || DIRECTION_COLORS.default;
}

/** Status display constants */
export const STATUS_COLORS = {
  active: '#16a34a',
  paused: '#ca8a04',
  archived: '#6b7280',
};
export const STATUS_LABELS = {
  active: 'assistant_status.active',
  paused: 'assistant_status.paused',
  archived: 'assistant_status.archived',
};

/** Usage bar component */
export function UsageBar({ used, limit, color }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color || '#6366f1' }} />
    </div>
  );
}

/** Tier badge component */
export function TierBadge({ tier }) {
  const t = useT();
  const map = {
    free: { bg: '#f3f4f6', color: '#6b7280', label: t('tiers.free') },
    pro: { bg: '#fef3c7', color: '#92400e', label: t('tiers.pro') },
    business: { bg: '#ecfdf5', color: '#065f46', label: t('tiers.business') },
  };
  const s = map[tier] || map.free;
  return <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
}

/** Status badge component */
export function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  const label = STATUS_LABELS[status] || status;
  return <span className="inline-flex items-center gap-1 text-xs"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />{label}</span>;
}

export function StatusBadgeI18n({ status, t }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  const key = STATUS_LABELS[status] || status;
  const label = t ? t(key) : key;
  return <span className="inline-flex items-center gap-1 text-xs"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />{label}</span>;
}

/** Tier badge for wallet tier display — call with t function for i18n */
export function tierBadge(tier, t) {
  const styles = {
    standard: { bg: '#f0f9ff', color: '#1e40af', key: 'tiers.standard' },
    tier1: { bg: '#f0fdf4', color: '#166534', key: 'tiers.tier1' },
    tier2: { bg: '#fef2f2', color: '#991b1b', key: 'tiers.tier2' },
  };
  const s = styles[tier] || styles.standard;
  const label = t ? t(s.key) : s.key;
  return (
    <span className="px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.color }}>
      {label}
    </span>
  );
}
